import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { buildPrintPreviewHtml, sanitizePdfFileNamePart } from '@/utils/printPreview'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: Outbound Logistics Debit Note PRINT reports
// (rpCtrlSalesDebitNote{Weight,Quality}Claim). One reusable PRESENTATIONAL
// dialog — the HTML arrives via props (the Frame owns the preview query; the BE
// resolves Weight/Quality from the row claim_type). Mirrors CreditNotePrintDialog:
// iframe srcDoc preview + Print button (hidden when !canEdit) →
// printAndUploadReport, responsive A4 fit (skill /responsive-style + /convert-pr).

interface DebitNotePrintDialogProps {
  open: boolean
  /** Dialog title (e.g. "Print Preview - Debit - Weight (DN1/INV0002.F)"). */
  title: string
  /** HTML preview (parent-fetched). */
  html: string
  /** Fetching HTML (overlay spinner). */
  loading: boolean
  /** View-only accounts: hide the real Print button (still see the preview). */
  canEdit?: boolean
  onClose: () => void
}

export default function DebitNotePrintDialog({
  open,
  title,
  html,
  loading,
  canEdit = true,
  onClose,
}: DebitNotePrintDialogProps) {
  const [toastMessage, setToastMessage] = useState('')
  const iframeTitle = 'Debit Note Print Preview'
  const previewHtml = html ? buildPrintPreviewHtml(html, { styleId: 'debit-note-print-preview-overrides' }) : ''

  return (
    <>
      {open && loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog
        open={open && Boolean(html)}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={title}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            {title.replace(/^Print Preview - /, '')}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} size="small">
              Cancel
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                disabled={!html}
                onClick={() => {
                  const idPart = title.replace(/^.*\(([^)]+)\).*$/, '$1')
                  // Tên file PDF = chỉ mã debit (vd "DN0268-INV4074.F.pdf") — bỏ hậu tố _Ctrl_Sales_Debit_Note rườm rà.
                  const printTitle = `${sanitizePdfFileNamePart(idPart || 'debit_note')}.pdf`
                  void printAndUploadReport({
                    iframeTitle,
                    reportName: 'rpCtrlSalesDebitNote',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
